import { useIntl } from '@capsicum/common';
import { Divider } from '@capsicum/common-web';
import { WalletSuccessfulWithdrawStatus } from '@capsicum/features/wallet/shared';
import styled from 'styled-components';

export type WithdrawResumeProps = {
	amount?: string;
	reference?: string;
	status: WalletSuccessfulWithdrawStatus;
};

export const WithdrawResume = ({ amount, reference, status }: WithdrawResumeProps) => {
	const { $t } = useIntl();

	return (
		<S.MainContainer>
			<S.Amount>{amount}</S.Amount>
			{$t('wallet.withdraw.processingDrawer.subtitle')}
			<S.Content>
				{reference && (
					<>
						<S.Section>
							<S.SectionName>Reference:</S.SectionName>
							<S.SectionValue>{reference}</S.SectionValue>
						</S.Section>
						<Divider orientation="horizontal" />
					</>
				)}
				<S.Section>
					<S.SectionName>Status:</S.SectionName>
					<S.SectionValue>
						<S.StatusContainer>
							<S.Dot $status={status} />
							{status?.charAt(0)?.toUpperCase() + status?.substring(1)}
						</S.StatusContainer>
					</S.SectionValue>
				</S.Section>
			</S.Content>
			<S.Description>{$t('wallet.withdraw.processingDrawer.description')}</S.Description>
		</S.MainContainer>
	);
};

namespace S {
	export const MainContainer = styled.div`
		${({ theme }) => theme.border.radius.medium};
		text-align: center;
		padding: 0 ${({ theme }) => theme.spacings.medium};
		margin: ${({ theme }) => theme.spacings.medium} 0;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
	export const Content = styled.div`
		${({ theme }) => theme.border.radius.medium};
		background-color: ${({ theme }) => theme.colors.backgrounds.status.empty};
		padding: 0 ${({ theme }) => theme.spacings.medium};
		margin: ${({ theme }) => theme.spacings.medium} 0;
	`;
	export const Amount = styled.div`
		${({ theme }) => theme.fonts.text.text1};
	`;
	export const Description = styled.div`
		margin: ${({ theme }) => theme.spacings.medium} 0;
	`;
	export const Section = styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;
	export const SectionName = styled.p`
		${({ theme }) => theme.fonts.text.text3};
	`;
	export const SectionValue = styled.div`
		${({ theme }) => theme.fonts.text.text1};
	`;
	export const StatusContainer = styled.div`
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;
	export const Dot = styled.div<{ $status: WalletSuccessfulWithdrawStatus }>`
		${({ theme }) => theme.border.radius.circle};
		width: ${({ theme }) => theme.sizing.xxxsmall};
		height: ${({ theme }) => theme.sizing.xxxsmall};
		background-color: ${({ $status, theme }) =>
			($status === WalletSuccessfulWithdrawStatus.PROCESSING &&
				theme.colors.backgrounds.status.partiallyCorrect) ||
			(($status === WalletSuccessfulWithdrawStatus.PROCESSED ||
				$status === WalletSuccessfulWithdrawStatus.PENDING) &&
				theme.colors.backgrounds.status.available) ||
			(($status === WalletSuccessfulWithdrawStatus.FAILED ||
				$status === WalletSuccessfulWithdrawStatus.ERROR ||
				$status === WalletSuccessfulWithdrawStatus.REJECTED) &&
				theme.colors.backgrounds.status.incorrect)};
	`;
}
